import React, { useState } from 'react';

import ChevronDown from '@gravity-ui/icons/ChevronDown';
import Plus from '@gravity-ui/icons/Plus';
import { Button, Icon, SegmentedRadioGroup, Select, TextArea, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { IconMedium } from 'client/blocks/AppIcons/AppIcons';
import { RevisionControl } from 'client/blocks/ControlPanel/RevisionControl';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { BackgroundImageUploader } from 'client/blocks/FilesUploaders/BackgroundImageUploader';
import { FileUploaderInput } from 'client/blocks/FilesUploaders/FilesUploaders';
import { InstallableAppLogoUploader } from 'client/blocks/FilesUploaders/InstallableAppLogoUploader';
import { LogoUploader } from 'client/blocks/FilesUploaders/LogoUploader';
import { ApkFormFields } from 'client/blocks/form/ApkFormFields/ApkFormFields';
import { BrandVerification } from 'client/blocks/form/BrandVerification/BrandVerification';
import { EmailField } from 'client/blocks/form/EmailField/EmailField';
import { Field } from 'client/blocks/form/Field/Field';
import { FormCard } from 'client/blocks/form/FormCard/FormCard';
import { InstallableAppAccessField } from 'client/blocks/form/InstallableAppAccessField/InstallableAppAccessField';
import { ScreenshotsBlock } from 'client/blocks/form/ScreenShots/ScreenshotsBlock';
import { VoiceControl } from 'client/blocks/form/VoiceControl/VoiceControl';
import { RevisionAction } from 'client/blocks/RevisionActions/RevisionActions';
import { RevisionNote } from 'client/blocks/statuses/Notes/RevisionNote';
import { lengthConfig } from 'client/lib/validation';
import { SkillAccess } from 'client/model/settings';
import { Channel, InstallableApp } from 'client/model/skill';
import { getCanEditApp, getCanViewAsAdmin } from 'client/utils';
import { mapInstallableAppSettingsToValues } from 'client/utils/settings/installable-app';
import { installableAppLabels as l } from 'client/utils/settings/labels';
import * as keyset from 'i18n/AppForm';
import * as TvAppIndexKeyset from 'i18n/AppsIndex';

import styles from './Settings.module.scss';

const t = i18n(keyset);
const tTvAppIndex = i18n(TvAppIndexKeyset);

interface Props {
    revision: InstallableApp['revisions'][number];
    app: InstallableApp;
    setInstallableApp: (app: InstallableApp) => void;
}

export const InstallableAppRevision: React.FC<Props> = ({ revision, app, setInstallableApp }) => {
    const [openCardName, setOpenCardName] = useState('');
    const canViewAsAdmin = getCanViewAsAdmin();

    const revisionActionDisabled = !getCanEditApp(app);

    const handleMobileCardClick = (tabName: string) => {
        if (tabName === openCardName) {
            setOpenCardName('');
        } else {
            setOpenCardName(tabName);
        }
    };

    const values = mapInstallableAppSettingsToValues(revision);

    return (
        <ErrorBoundary withHomepageLink>
            <div>
                <div className={styles.header}>
                    <div className={styles.header__container}>
                        <div className={styles.appTitle}>
                            <IconMedium imageUrl={values.squareLogoUrl || values.logoUrl} />
                            <h1 className={styles.appTitle__header}>{app.onAir ? app.name : app.draft.name}</h1>
                        </div>
                        <RevisionAction installableApp={app} setInstallableApp={setInstallableApp}>
                            <RevisionControl revisionId={revision.id} disabled={revisionActionDisabled} />
                        </RevisionAction>
                    </div>
                    <RevisionNote publishedAt={revision.publishedAt} />
                </div>
                <div className={styles.mobileFormHeader}>
                    <ChevronDown />
                    {tTvAppIndex('main-information')}
                </div>
                <FormCard
                    cardKey="about"
                    cardTitle={t('about')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field required fieldTitle={t('app-name-title')} fieldDescription={t('app-name-description')}>
                        <TextInput
                            autoComplete={false}
                            className={styles.formElement}
                            disabled
                            placeholder={t('app-name-placeholder')}
                            size="xl"
                            value={revision.name}
                        />
                    </Field>
                    <Field fieldTitle={l.activationPhrase} fieldDescription={l.activationPhrases}>
                        {revision.activationPhrases.map((phraseText, phraseIndex) => (
                            <TextInput
                                className={styles.formElement}
                                errorMessage={''}
                                key={phraseIndex}
                                value={phraseText}
                                disabled
                                size="xl"
                            />
                        ))}
                        <Button className={styles.formButton} disabled>
                            <Icon data={Plus} />
                            {t('add')}
                        </Button>
                    </Field>
                    <Field
                        required
                        fieldTitle={t('short-description-title')}
                        fieldDescription={t('short-description-description')}
                    >
                        <TextInput
                            autoComplete={false}
                            className={styles.formElement}
                            disabled
                            placeholder={t('short-description-placeholder')}
                            size="xl"
                            value={revision.publishingSettings.secondaryTitle}
                        />
                    </Field>
                    <Field required fieldTitle={t('description-title')} fieldDescription={t('description-description')}>
                        <TextArea
                            className={styles.formElement}
                            disabled
                            note={
                                <span className={styles.grayNote}>
                                    {lengthConfig.description - (revision.publishingSettings.description?.length || 0)}
                                </span>
                            }
                            rows={4}
                            size="xl"
                            value={revision.publishingSettings.description}
                        />
                    </Field>
                    <Field fieldTitle={t('category-title')} required>
                        <Select
                            className={styles.formElement}
                            disabled
                            placeholder={t('category-placeholder')}
                            size="xl"
                            value={[revision.publishingSettings.category || '']}
                        />
                    </Field>
                    <Field
                        fieldTitle={t('age-restriction-title')}
                        fieldDescription={t('age-restriction-description')}
                        required
                    >
                        <Select
                            className={styles.formElement}
                            disabled
                            options={
                                revision.ageRestriction
                                    ? [{ value: revision.ageRestriction, content: revision.ageRestriction }]
                                    : []
                            }
                            placeholder={t('age-restriction-placeholder')}
                            size="xl"
                            value={revision.ageRestriction ? [revision.ageRestriction] : undefined}
                        />
                    </Field>
                </FormCard>
                <FormCard
                    cardKey="application"
                    cardTitle={t('application')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field fieldTitle={l.developerName}>
                        <TextInput
                            className={styles.formElement}
                            disabled
                            size="xl"
                            value={revision.publishingSettings.developerName}
                        />
                    </Field>
                    <EmailField
                        disabled
                        value={revision.publishingSettings.email}
                        isVerified={!!revision.publishingSettings.emailIsVerified}
                    />
                    <BrandVerification
                        brandIsVerified={revision.publishingSettings.brandIsVerified}
                        disabled
                        brandVerificationWebsite={revision.publishingSettings.brandVerificationWebsite}
                    />
                    <InstallableAppAccessField
                        installableAppAccess={revision.skillAccess}
                        disabled
                        channel={Channel.TvApp}
                    />
                    <VoiceControl
                        disabled
                        voiceControlEnable={!revision.backendSettings?.isVoiceControlDisabled}
                        isAdvancedVoiceControl={!!revision.backendSettings?.isAdvancedVoiceControlSupported}
                    />
                    {canViewAsAdmin && (
                        <Field fieldTitle={'isTrusted'}>
                            <SegmentedRadioGroup
                                className={styles.formButton}
                                disabled
                                options={[
                                    { value: 'True', content: 'True' },
                                    { value: 'False', content: 'False' },
                                ]}
                                value={revision.isTrusted ? 'True' : 'False'}
                                size="xl"
                            />
                        </Field>
                    )}
                    <Field required fieldTitle={t('packet-name-title')} fieldDescription={t('packet-name-description')}>
                        <TextInput
                            className={styles.formElement}
                            disabled
                            placeholder={t('packet-name-placeholder')}
                            size="xl"
                            value={app.packageName}
                        />
                    </Field>
                    <ApkFormFields
                        slug={app.slug}
                        apkInfos={revision.apkInfosWithTargets}
                        multiApkEnabled={revision.multiApkEnabled}
                        disabled
                    />
                </FormCard>
                <FormCard
                    cardKey="assets"
                    cardTitle={t('design')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <InstallableAppLogoUploader disabled logoUrl={revision.logoUrl} />
                    <Field required fieldTitle={t('icon-title')} fieldDescription={t('square-icon-description')}>
                        <LogoUploader logoType="square" logoUrl={values.squareLogoUrl} disabled />
                    </Field>
                    {app.channel === Channel.SmartScreenApp && (
                        <Field
                            fieldTitle={t('secondary-icon-title')}
                            fieldDescription={t('secondary-icon-description')}
                        >
                            <LogoUploader logoType="secondary" logoUrl={revision.secondaryLogoUrl} disabled />
                        </Field>
                    )}
                    <BackgroundImageUploader aliceApp={app} values={values} disabled />
                    <ScreenshotsBlock
                        disabled
                        fieldTitle={t('screenshots-title')}
                        fieldDescription={t('screenshots-description')}
                        items={
                            revision.screenshots
                                ? revision.screenshots.map((img) => ({ imageId: img.imageUrl, imageUrl: img.imageUrl }))
                                : []
                        }
                        updateScreenshots={() => {}}
                        error=""
                        setErrors={() => {}}
                    />
                </FormCard>
                <FormCard
                    cardKey="agreement"
                    cardTitle={t('agreement')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field
                        required={revision.skillAccess === SkillAccess.Public}
                        fieldTitle={t('eula-title')}
                        fieldDescription={t('eula-description')}
                    >
                        <FileUploaderInput
                            disabled
                            onUploadComplete={() => {}}
                            fieldName={'eulaAgreement'}
                            uploadedData={revision.eulaAgreement}
                            error=""
                            setErrors={() => {}}
                        />
                    </Field>
                    <Field
                        required={revision.skillAccess === SkillAccess.Public}
                        fieldTitle={t('private-policy-title')}
                        fieldDescription={t('private-policy-description')}
                    >
                        <FileUploaderInput
                            disabled
                            onUploadComplete={() => {}}
                            fieldName={'privacyAgreement'}
                            uploadedData={revision.privacyAgreement}
                            error=""
                            setErrors={() => {}}
                        />
                    </Field>
                </FormCard>
            </div>
        </ErrorBoundary>
    );
};
